import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createColumnHelper, ColumnDef } from '@tanstack/react-table'
import { SimpleTable } from 'components/tables'
import { TwoColumn } from 'components/layout'
import { Spinner } from 'components/loaders'
import { useUserSSO } from '@hooks/useUserSSO'
import { LinkedStatus } from './LinkedStatus'

interface SSOSectionProps {
  userId: number
}

interface SSORow {
  providerName: string
  userId: string | null
}

const columnHelper = createColumnHelper<SSORow>()

export const SSOSection: FC<SSOSectionProps> = ({ userId }) => {
  const { t } = useTranslation()
  const { data: sso } = useUserSSO(userId)

  const data: SSORow[] = useMemo(
    () => (sso ? [{ providerName: sso.providerName, userId: sso.userId }] : []),
    [sso],
  )

  const columns = [
    columnHelper.accessor('providerName', {
      header: t('features.usersAdmin.userDetails.ssoProvider'),
    }),
    columnHelper.accessor('userId', {
      header: t('features.usersAdmin.userDetails.ssoId'),
      // Users aren't linked to their SSO profile until they first sign in
      cell: ({ getValue }) => getValue() ?? <LinkedStatus linked={false} />,
    }),
  ] as ColumnDef<SSORow>[]

  return (
    <TwoColumn
      label={t('features.usersAdmin.userDetails.sso')}
      description={t('features.usersAdmin.userDetails.ssoDescription')}
    >
      {!sso ? (
        <div className="w-[825px]">
          <Spinner />
        </div>
      ) : (
        <SimpleTable
          className="w-[825px]"
          columns={columns}
          data={data}
          defaultSortColumn="providerName"
        />
      )}
    </TwoColumn>
  )
}
